/**
 * useAIFiles — normalized file store for AIFileUploadV2 (AC-2.7).
 *
 * Design decisions:
 *  - T5-1: Normalized store Record<id, item> + fileIds array prevents
 *    full-list re-renders when a single file is patched.
 *  - T5-34: AbortController on every fetch.
 *  - T5-29: Functional setState in WS event handlers.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useAuth from 'src/hooks/useAuth';
import {
  listAIFiles,
  uploadAIFile,
  deleteAIFile,
} from 'src/services/aiFileUploadV2';
import type {
  OnFileDocument,
  UploadAIFileOutcome,
  UploadAIFileResult,
} from 'src/services/aiFileUploadV2';
import type {
  CrossMemberDocument,
  FileListMeta,
  LinkedSurveyFileItem,
  RenterFileListItem,
  RenterFileStatus,
} from 'src/types/aiFileUploadV2';

// Suppress unused import warning from React (needed for JSX in test consumers)
void React;

const HIDDEN_STATUSES: RenterFileStatus[] = ['cancelled', 'failed', 'rejected'];
const WS_DRIVEN_STATUSES: Set<unknown> = new Set([
  'processing',
  'extracting',
  'classified',
  'completed',
  'awaiting_set_validation',
  'needs_classification',
  'needs_member_attribution',
  'needs_member_confirmation',
  'failed',
  'rejected',
  'cancelled',
]);

export interface UploadFilesOptions {
  targetQitId?: number;
  targetAnswerGroupId?: number;
  questionGroupId?: number;
}

export interface UploadFilesResult {
  fileIds: number[];
  createdAnswerGroupId?: number;
  /** Documents the backend linked/left on file instead of creating a row. */
  linked: LinkedSurveyFileItem[];
  alreadyOnFile: OnFileDocument[];
  /** Rows already in flight/awaiting that the upload did not duplicate. */
  processing: RenterFileListItem[];
  existing: RenterFileListItem[];
  /** ENG-4086: copies already on another member's survey of this cert. */
  crossMember?: CrossMemberDocument[];
}

export interface UseAIFilesResult {
  fileMap: Record<number, RenterFileListItem>;
  fileIds: number[];
  meta: FileListMeta | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  uploadFiles: (
    surveyId: number,
    files: File[],
    options?: UploadFilesOptions
  ) => Promise<UploadFilesResult>;
  removeFile: (fileId: number) => Promise<void>;
  applyWSPatch: (
    id: number,
    patch: Partial<RenterFileListItem>,
    seed?: Partial<RenterFileListItem>,
    deleteFromList?: boolean
  ) => void;
  /** File IDs that failed during this WS session (not pre-existing failures from prior loads) */
  liveFailedIds: Set<number>;
  /** Failed file data persisted separately from fileMap so refetches don't clear errors. */
  failedEntries: Record<number, RenterFileListItem>;
  /** Only advances completed_steps — never decrements (steps run out of order) */
  applyStepCompleted: (id: number, stepNumber: number) => void;
}

export function useAIFiles(
  surveyId: number | null,
  userId?: number,
  targetAnswerGroupId?: number,
  targetQitId?: number | null,
  /**
   * Skip the initial file-list fetch. Used by the interview embed for a
   * brand-new multi-group instance (answer group not yet created): the
   * unscoped fetch would return other instances' files for the same QIT.
   * Files uploaded during the session still appear via uploadFiles/WS.
   */
  skipInitialFetch?: boolean
): UseAIFilesResult {
  const { user } = useAuth();
  const [fileMap, setFileMap] = useState<Record<number, RenterFileListItem>>(
    {}
  );
  const [fileIds, setFileIds] = useState<number[]>([]);
  const [meta, setMeta] = useState<FileListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // IDs of files that failed during this WS session — not pre-existing failures
  const [liveFailedIds, setLiveFailedIds] = useState<Set<number>>(new Set());
  const [failedEntries, setFailedEntries] = useState<
    Record<number, RenterFileListItem>
  >({});

  const abortRef = useRef<AbortController | null>(null);
  const surveyIdRef = useRef<number | null>(surveyId);
  surveyIdRef.current = surveyId;
  const userIdRef = useRef<number | undefined>(userId);
  userIdRef.current = userId;
  const targetAnswerGroupIdRef = useRef<number | undefined>(
    targetAnswerGroupId
  );
  targetAnswerGroupIdRef.current = targetAnswerGroupId;
  const targetQitIdRef = useRef<number | null | undefined>(targetQitId);
  targetQitIdRef.current = targetQitId;
  const liveFailedIdsRef = useRef<Set<number>>(liveFailedIds);
  liveFailedIdsRef.current = liveFailedIds;
  const fileMapRef = useRef<Record<number, RenterFileListItem>>(fileMap);
  fileMapRef.current = fileMap;

  const fetchFiles = useCallback(() => {
    if (!surveyIdRef.current) {
      return;
    }
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setLoading(true);
    setError(null);

    listAIFiles(
      surveyIdRef.current,
      signal,
      userIdRef.current,
      targetAnswerGroupIdRef.current,
      targetQitIdRef.current
    )
      .then((data) => {
        const newMap: Record<number, RenterFileListItem> = {};
        const newIds: number[] = [];
        for (const file of data.files) {
          newMap[file.id] = file;
          if (!HIDDEN_STATUSES.includes(file.status)) {
            newIds.push(file.id);
          } else if (liveFailedIdsRef.current.has(file.id)) {
            newIds.push(file.id);
          }
        }
        setFileMap(newMap);
        setFileIds(newIds);
        setMeta(data.meta);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name === 'CanceledError' || err.name === 'AbortError') {
          return;
        }
        setError(err.message ?? 'Failed to load files');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!surveyId) {
      return;
    }
    if (skipInitialFetch) {
      // Brand-new multi-group instance: no prior files by definition. Skip the
      // unscoped fetch (which would return other instances' files for this
      // QIT) and present an empty, non-loading list.
      setLoading(false);
      return;
    }
    fetchFiles();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchFiles, surveyId, user?.id, skipInitialFetch]);

  // T5-29: functional setState in WS patch handler
  // seed: optional base data to create a stub when the file isn't in the map yet
  const applyWSPatch = useCallback(
    (
      id: number,
      patch: Partial<RenterFileListItem>,
      seed?: Partial<RenterFileListItem>,
      deleteFromList?: boolean
    ) => {
      // WS scope guard: reject events for files outside this scope.
      // Files already in the map were admitted by a scope-checked path
      // (REST fetch filtered by target_qit_id, or uploadFiles which sets it).
      if (!fileMapRef.current[id]) {
        const fileQitId =
          (patch as Record<string, unknown>).target_qit_id ??
          (seed as Record<string, unknown>)?.target_qit_id ??
          null;
        const matchesScope =
          targetQitIdRef.current != null
            ? fileQitId === targetQitIdRef.current
            : fileQitId == null;
        if (!matchesScope) {
          return;
        }
      }

      setFileMap((prev) => {
        if (!prev[id]) {
          if (seed) {
            // Seed the file with the data from the WS event so no fetch is needed
            const stub = { ...seed, ...patch, id } as RenterFileListItem;
            return { ...prev, [id]: stub };
          }
          return prev;
        }
        return { ...prev, [id]: { ...prev[id], ...patch } };
      });
      // Failed files live in `failedEntries`, not `fileMap` (they're removed
      // from the map on failure), so mirror the escalation reconciliation
      // there for the error banners.
      if (patch.user_escalated !== undefined) {
        setFailedEntries((prev) =>
          prev[id]
            ? {
                ...prev,
                [id]: { ...prev[id], user_escalated: patch.user_escalated },
              }
            : prev
        );
      }
      // Remove from fileMap and fileIds when deleteFromList is set.
      // Save to failedEntries first so error banners have the data.
      if (deleteFromList && patch.status === 'failed') {
        const existing = fileMapRef.current[id];
        setFailedEntries((prev) => {
          // First blocking failure wins — a later processing_failed must not
          // clobber the richer error fields from an earlier step_failed.
          if (prev[id]) {
            return prev;
          }
          // Seed a minimal stub when the failure arrives before the file
          // exists in fileMap (fast step_failed before processing_started),
          // so the user still gets an error banner.
          const base =
            existing ??
            ({
              id,
              original_filename: '',
              created_at: new Date().toISOString(),
              ...seed,
            } as RenterFileListItem);
          return { ...prev, [id]: { ...base, ...patch } };
        });
        setFileMap((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        fileMapRef.current = { ...fileMapRef.current };
        delete fileMapRef.current[id];
        // Update the ref immediately — seedResult may run before re-render.
        liveFailedIdsRef.current = new Set([...liveFailedIdsRef.current, id]);
        setLiveFailedIds((prev) => new Set([...prev, id]));
        setFileIds((prev) => prev.filter((fid) => fid !== id));
        return;
      }
      // Also add to fileIds if not already present and status is not hidden
      if (
        !HIDDEN_STATUSES.includes(
          (patch.status ?? seed?.status) as RenterFileStatus
        )
      ) {
        setFileIds((prev) => {
          if (prev.includes(id)) {
            return prev;
          }
          return [...prev, id];
        });
      } else if (patch.status) {
        // Remove from fileIds when transitioning to hidden status
        setFileIds((prev) => prev.filter((fid) => fid !== id));
      }
    },
    []
  );

  // Never decrement completed_steps — steps can run out of order
  const applyStepCompleted = useCallback((id: number, stepNumber: number) => {
    setFileMap((prev) => {
      if (!prev[id]) {
        return prev;
      }
      const current = prev[id].completed_steps ?? 0;
      if (stepNumber <= current) {
        return prev;
      } // already at or past this step
      return { ...prev, [id]: { ...prev[id], completed_steps: stepNumber } };
    });
  }, []);

  const seedResult = useCallback((r: UploadAIFileResult) => {
    // If this file was already auto-deleted (WS step_failed arrived before
    // the upload HTTP response), skip re-seeding the queue row — but
    // backfill the failed entry's filename so the error banner shows it.
    if (liveFailedIdsRef.current.has(r.id)) {
      setFailedEntries((prev) =>
        prev[r.id] && !prev[r.id].original_filename
          ? {
              ...prev,
              [r.id]: { ...prev[r.id], original_filename: r.filename },
            }
          : prev
      );
      return;
    }
    setFileMap((prev) => {
      if (prev[r.id] && prev[r.id].status === 'failed') {
        return prev;
      }
      const existing = prev[r.id];
      // Preserve any status already set by a WebSocket event. The WS may have
      // delivered processing_started (and possibly processing_completed) before
      // the upload HTTP response returned. Blindly setting status back to
      // 'pending' would leave the tile stuck in "Uploading..." state with no
      // further WS events to rescue it.
      const preserveExisting =
        existing && WS_DRIVEN_STATUSES.has(existing.status);

      const defaults = {
        id: r.id,
        uuid: r.uuid,
        original_filename: r.filename,
        status: 'pending' as RenterFileStatus,
        session_status: 'active' as const,
        total_steps: 0,
        completed_steps: 0,
        needs_manual_classification: false,
        is_duplicate: false,
        is_valid: null,
        match_count: 0,
        mime_type: '',
        file_size_bytes: 0,
        page_count: null,
        doc_category_key: null,
        doc_sub_type_key: null,
        doc_category_display: null,
        doc_sub_type_display: null,
        classification_confidence: null,
        classification_method: null,
        current_step: null,
        error_message: null,
        error_step: null,
        error_detail: null,
        rejection_reason_category: null,
        target_qit_id: targetQitIdRef.current ?? null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (preserveExisting) {
        return {
          ...prev,
          [r.id]: {
            ...defaults,
            ...existing,
            id: r.id,
            uuid: r.uuid,
            original_filename: r.filename,
            updated_at: new Date().toISOString(),
          },
        };
      }

      return { ...prev, [r.id]: defaults };
    });
    setFileIds((prev) => (prev.includes(r.id) ? prev : [...prev, r.id]));
  }, []);

  // ENG-3331: a blocked re-upload (no new row) is surfaced directly in the
  // error banners by seeding it into failedEntries.
  const seedBlocked = useCallback((blocked: RenterFileListItem) => {
    setFailedEntries((prev) => ({ ...prev, [blocked.id]: blocked }));
  }, []);

  const uploadFiles = useCallback(
    async (
      surveyId: number,
      files: File[],
      options?: UploadFilesOptions
    ): Promise<UploadFilesResult> => {
      const ac = new AbortController();
      const {
        targetQitId,
        targetAnswerGroupId: optGroupId,
        questionGroupId,
      } = options ?? {};
      try {
        let resolvedGroupId = optGroupId;
        let createdAnswerGroupId: number | undefined;
        const allResults: UploadAIFileResult[] = [];
        const linked: LinkedSurveyFileItem[] = [];
        const alreadyOnFile: OnFileDocument[] = [];
        const processing: RenterFileListItem[] = [];
        const existing: RenterFileListItem[] = [];
        const crossMember: CrossMemberDocument[] = [];

        // ENG-3331: a blocked re-upload returns no new created file; surface the
        // existing failed file in the error banner instead.
        const handleOutcome = (outcome: UploadAIFileOutcome): void => {
          outcome.blocked.forEach(seedBlocked);
          // Tolerate older/partial responses (deploy window and unit fixtures).
          linked.push(...(outcome.linked ?? []));
          alreadyOnFile.push(...(outcome.alreadyOnFile ?? []));
          processing.push(...(outcome.processing ?? []));
          existing.push(...(outcome.existing ?? []));
          crossMember.push(...(outcome.crossMember ?? []));
          // During the deploy window Thor also echoes a blocked row into
          // `created` so pre-block client bundles don't index undefined. Skip
          // those ids here so the file only lands in the error banner.
          const blockedIds = new Set(outcome.blocked.map((b) => b.id));
          outcome.created
            .filter(
              (created) => !created.compat_echo && !blockedIds.has(created.id)
            )
            .forEach((created) => {
              seedResult(created);
              allResults.push(created);
            });
        };

        // If no answer group yet but question_group_id is provided (lazy
        // creation), upload the first file alone to get the created group ID,
        // then upload the rest in parallel with that ID.
        const needsLazyCreation = !resolvedGroupId && questionGroupId != null;

        if (needsLazyCreation && files.length > 0) {
          const firstOutcome = await uploadAIFile({
            surveyId,
            file: files[0],
            targetQitId,
            questionGroupId,
            signal: ac.signal,
          });
          handleOutcome(firstOutcome);
          if (firstOutcome.created[0]?.target_answer_group_id) {
            resolvedGroupId = firstOutcome.created[0].target_answer_group_id;
            createdAnswerGroupId =
              firstOutcome.created[0].target_answer_group_id;
          }

          if (files.length > 1) {
            const rest = await Promise.all(
              files.slice(1).map((file) =>
                uploadAIFile({
                  surveyId,
                  file,
                  targetQitId,
                  targetAnswerGroupId: resolvedGroupId,
                  signal: ac.signal,
                })
              )
            );
            rest.forEach(handleOutcome);
          }
        } else {
          const results = await Promise.all(
            files.map((file) =>
              uploadAIFile({
                surveyId,
                file,
                targetQitId,
                targetAnswerGroupId: resolvedGroupId,
                signal: ac.signal,
              })
            )
          );
          results.forEach(handleOutcome);
        }

        return {
          fileIds: allResults.map((r) => r.id),
          createdAnswerGroupId,
          linked,
          alreadyOnFile,
          processing,
          existing,
          crossMember,
        };
      } catch (err: unknown) {
        const e = err as Error;
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          setError(e.message ?? 'Upload failed');
        }
        return {
          fileIds: [],
          linked: [],
          alreadyOnFile: [],
          processing: [],
          existing: [],
          crossMember: [],
        };
      }
    },
    [seedResult, seedBlocked]
  );

  const removeFile = useCallback(async (fileId: number) => {
    const ac = new AbortController();
    try {
      await deleteAIFile(fileId, ac.signal);
      setFileMap((prev) => {
        const next = { ...prev };
        delete next[fileId];
        return next;
      });
      setFileIds((prev) => prev.filter((fid) => fid !== fileId));
      setLiveFailedIds((prev) => {
        if (!prev.has(fileId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
      // Also drop the failed entry so error banners and the contact-info
      // gate don't keep showing for a file that no longer exists.
      setFailedEntries((prev) => {
        if (!(fileId in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[fileId];
        return next;
      });
    } catch (err: unknown) {
      const e = err as Error;
      if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
        setError(e.message ?? 'Delete failed');
      }
    }
  }, []);

  return {
    fileMap,
    fileIds,
    meta,
    loading,
    error,
    refetch: fetchFiles,
    uploadFiles,
    removeFile,
    applyWSPatch,
    liveFailedIds,
    failedEntries,
    applyStepCompleted,
  };
}
